import React, { useState, useEffect, useRef, useMemo } from "react";
import { Icon, PrimaryButton, DefaultButton, TooltipHost, DirectionalHint, Link } from "@fluentui/react";
import { useResponsive } from "./useResponsive";
import { ScenarioDetailsPanel } from "./ScenarioDetailsPanel";
import { logEvent } from "../../helper/AppInsightsHelper";
import { useGetPromptLibraryQuery } from "../../slices/AIHub.slice";
import { PromptLibraryItem } from "../../interfaces/slices.interfaces";

interface ScenarioFeature {
    supportedFeatures: string[];
    unsupportedFeatures: string[];
}

interface Scenario {
    scenarioId: number;
    scenarioGrouping: string;
    scenarioName: string;
    description: string;
    samplePrompts?: string[];
    suggestedPrompts?: string; // API returns this as pipe-separated string
    state?: string;
    supportedFeatures?: string; // API returns this as pipe-separated string
    unsupportedFeatures?: string; // API returns this as pipe-separated string
    promptCategory?: string; // Category for grouping prompts
}

// Helper function to parse scenario features from API data
const parseScenarioFeatures = (scenarios: Scenario[]): { [key: number]: ScenarioFeature } => {
    const features: { [key: number]: ScenarioFeature } = {};

    scenarios.forEach(scenario => {
        features[scenario.scenarioId] = {
            supportedFeatures: scenario.supportedFeatures
                ? scenario.supportedFeatures.split("|").map(f => f.trim()).filter(f => f.length > 0)
                : [],
            unsupportedFeatures: scenario.unsupportedFeatures
                ? scenario.unsupportedFeatures.split("|").map(f => f.trim()).filter(f => f.length > 0)
                : []
        };
    });

    return features;
};

interface ScenarioSelectionProps {
    scenarios: Scenario[];
    scenariosLoading: boolean;
    selectedScenario: number | null;
    onScenarioSelect: (scenarioId: number) => void;
    onNext: () => void;
    // New props for input handling
    onPromptSelect?: (prompt: string) => void;
    ChatInput?: React.ComponentType<any>;
    inputProps?: any;
    // Props to pass scenario info
    onSetScenarioInfo?: (scenarioId: number, scenarioName: string, scenarioGrouping?: string) => void;
    // Selected scenario name from parent
    selectedScenarioName?: string;
    directChat?: boolean;
}

// Get sample prompts from API response for specific scenario
const getSamplePrompts = (scenarioId: number, scenarios: Scenario[]): string[] => {
    const scenario = scenarios.find(s => s.scenarioId === scenarioId);

    if (scenario && scenario.samplePrompts && scenario.samplePrompts.length > 0) {
        return scenario.samplePrompts;
    }

    // If no samplePrompts in interface, check suggestedPrompts from API
    const scenarioWithSuggested = scenarios.find(s => s.scenarioId === scenarioId) as any;

    if (scenarioWithSuggested?.suggestedPrompts) {
        // Split by pipe symbol and filter out empty strings
        return scenarioWithSuggested.suggestedPrompts
            .split("|")
            .map((prompt: string) => prompt.trim())
            .filter((prompt: string) => prompt.length > 0);
    }

    // Fallback to empty array if no prompts available
    return [];
}; const ScenarioSelection: React.FC<ScenarioSelectionProps> = ({
    scenarios,
    scenariosLoading,
    selectedScenario,
    onScenarioSelect,
    onNext,
    onPromptSelect,
    ChatInput,
    inputProps,
    onSetScenarioInfo,
    selectedScenarioName,
    directChat = false
}) => {
    // All hooks must be at the top before any conditional returns
    const [showSamplePrompts, setShowSamplePrompts] = useState(false);
    const [currentPrompts, setCurrentPrompts] = useState<string[]>([]);
    const [showAllPrompts, setShowAllPrompts] = useState(false);
    const [localInputValue, setLocalInputValue] = useState("");
    const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({});
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedScenarioDetails, setSelectedScenarioDetails] = useState<{ id: number; name: string } | null>(null);
    const [selectedPromptCategory, setSelectedPromptCategory] = useState<string | null>(null);
    const isInitialMount = useRef(true);
    const containerRef = useRef<HTMLDivElement>(null);

    // Fetch prompt library data for category-based prompts
    const { data: promptLibraryData } = useGetPromptLibraryQuery();

    // Use responsive hook
    const { screenSize, size } = useResponsive();

    // Get the selected scenario's scenarioGrouping
    const selectedScenarioData = useMemo(() =>
        scenarios.find(s => s.scenarioId === selectedScenario),
        [scenarios, selectedScenario]
    );

    // Get scenarios in the same scenarioGrouping as selected scenario
    const scenariosInGroup = useMemo(() => {
        if (!selectedScenarioData?.scenarioGrouping) {
            return [];
        }
        return scenarios.filter(
            s => s.scenarioGrouping?.trim().toLowerCase() === selectedScenarioData.scenarioGrouping.trim().toLowerCase() &&
                (s.state === "Ready" || s.state === "Preview")
        );
    }, [scenarios, selectedScenarioData?.scenarioGrouping]);

    // Parse prompts from scenarios - each scenario's suggestedPrompts with its promptCategory
    interface ParsedScenarioPrompt {
        id: string;
        promptText: string;
        title: string;
        description: string;
        promptCategory: string;
        scenarioId: number;
    }

    const parsedScenarioPrompts = useMemo((): ParsedScenarioPrompt[] => {
        const prompts: ParsedScenarioPrompt[] = [];
        scenariosInGroup.forEach(scenario => {
            if (scenario.suggestedPrompts) {
                const promptTexts = scenario.suggestedPrompts.split("|").map(p => p.trim()).filter(p => p.length > 0);
                promptTexts.forEach((text, index) => {
                    const colonIndex = text.indexOf(":");
                    let title = text;
                    let description = text;
                    if (colonIndex > 0 && colonIndex < 60) {
                        title = text.substring(0, colonIndex).trim();
                        description = text.substring(colonIndex + 1).trim();
                    }
                    prompts.push({
                        id: `${scenario.scenarioId}-${index}`,
                        promptText: text,
                        title,
                        description,
                        promptCategory: scenario.promptCategory?.trim() || "",
                        scenarioId: scenario.scenarioId
                    });
                });
            }
        });
        return prompts;
    }, [scenariosInGroup]);

    // Check if the SELECTED scenario has promptCategory (use category-based UI only for these)
    const selectedScenarioHasCategory = useMemo(() => {
        return !!(selectedScenarioData?.promptCategory && selectedScenarioData.promptCategory.trim());
    }, [selectedScenarioData?.promptCategory]);

    // Get unique categories from scenarios in the group
    const promptCategories = useMemo(() => {
        const categories = new Set<string>();
        scenariosInGroup.forEach(scenario => {
            if (scenario.promptCategory && scenario.promptCategory.trim()) {
                categories.add(scenario.promptCategory.trim());
            }
        });
        // Sort categories alphabetically, but keep "None of these" at the end
        return Array.from(categories).sort((a, b) => {
            if (a === b) return 0;
            if (a === "None of these") return 1;
            if (b === "None of these") return -1;
            return a.localeCompare(b);
        });
    }, [scenariosInGroup]);

    // Auto-select first category when scenario changes or categories are loaded
    useEffect(() => {
        if (selectedScenarioHasCategory && promptCategories.length > 0) {
            setSelectedPromptCategory(promptCategories[0]);
        } else {
            setSelectedPromptCategory(null);
        }
    }, [selectedScenario, selectedScenarioHasCategory, promptCategories]);

    // Filter prompts by selected category
    const filteredParsedPrompts = useMemo(() => {
        if (!selectedPromptCategory) {
            return parsedScenarioPrompts;
        }
        // Treat "None of these" as a regular category since it exists in Kusto data
        return parsedScenarioPrompts.filter(p => p.promptCategory === selectedPromptCategory);
    }, [parsedScenarioPrompts, selectedPromptCategory]);

    // Legacy: Filter prompt library by selected scenario's scenarioGrouping (fallback)
    const scenarioPrompts = useMemo(() => {
        if (!promptLibraryData || !selectedScenarioData?.scenarioGrouping) {
            return [];
        }
        return promptLibraryData.filter(
            p => p.scenarioGrouping?.trim().toLowerCase() === selectedScenarioData.scenarioGrouping.trim().toLowerCase()
        );
    }, [promptLibraryData, selectedScenarioData?.scenarioGrouping]);

    // Legacy: Filter prompts by selected category (for prompt library fallback)
    const filteredScenarioPrompts = useMemo(() => {
        if (!selectedPromptCategory) {
            return scenarioPrompts;
        }
        if (selectedPromptCategory === "None of these") {
            return scenarioPrompts.filter(p => !p.promptCategory || !p.promptCategory.trim());
        }
        return scenarioPrompts.filter(p => p.promptCategory?.trim() === selectedPromptCategory);
    }, [scenarioPrompts, selectedPromptCategory]);

    // Parse prompt title from description (text before ":")
    const parsePromptTitle = (prompt: PromptLibraryItem): { title: string; description: string } => {
        const text = prompt.description || prompt.prompt || "";
        const colonIndex = text.indexOf(":");
        if (colonIndex === -1) {
            return { title: prompt.title || text, description: text };
        }
        return {
            title: text.substring(0, colonIndex).trim(),
            description: text.substring(colonIndex + 1).trim()
        };
    };

    // Parse scenario features from API data
    const scenarioDetails = React.useMemo(() => parseScenarioFeatures(scenarios), [scenarios]);

    // Group scenarios by scenarioGrouping - Filter to only show Ready and Preview scenarios
    // Also deduplicate scenarios by scenarioName within each group (show only unique names)
    const groupedScenarios = React.useMemo(() => {
        const groups: { [key: string]: Scenario[] } = {};
        // Filter scenarios to only include those with state "Ready" or "Preview"
        const filteredScenarios = scenarios.filter(scenario =>
            scenario.state === "Ready" || scenario.state === "Preview"
        );
        filteredScenarios.forEach(scenario => {
            // Trim whitespace from scenarioGrouping to avoid duplicate groups
            const group = (scenario.scenarioGrouping || "Other").trim();
            if (!groups[group]) {
                groups[group] = [];
            }
            // Only add if no scenario with the same name exists in this group
            const existingNames = groups[group].map(s => s.scenarioName.trim().toLowerCase());
            if (!existingNames.includes(scenario.scenarioName.trim().toLowerCase())) {
                groups[group].push(scenario);
            }
        });
        return groups;
    }, [scenarios]);

    useEffect(() => {
        if (selectedScenario !== null) {
            const prompts = getSamplePrompts(selectedScenario, scenarios);
            setCurrentPrompts(prompts);
        } else {
            setCurrentPrompts([]);
            // Reset to scenario selection view when selectedScenario is null (New Chat clicked)
            // But don't reset on initial mount as URL parameters might set it
            if (!isInitialMount.current) {
                setShowSamplePrompts(false);
            }
        }
    }, [selectedScenario, scenarios]);

    // Ensure prompts are loaded when showing the "Ask Me Anything" page
    useEffect(() => {
        if (showSamplePrompts && selectedScenario !== null && scenarios.length > 0) {
            const prompts = getSamplePrompts(selectedScenario, scenarios);
            // Always update prompts when scenarios data changes
            setCurrentPrompts(prompts);
        }
    }, [showSamplePrompts, selectedScenario, scenarios]);

    // Handle URL parameters for scenarioGroup and scenarioId - ONLY on initial mount
    React.useEffect(() => {
        if (directChat) {
            if (selectedScenario === null && scenarios.length > 0) {
                const scenario = scenarios[0];
                setLocalInputValue("");
                setShowAllPrompts(false);
                onScenarioSelect(scenario.scenarioId);
                onSetScenarioInfo?.(scenario.scenarioId, scenario.scenarioName, scenario.scenarioGrouping);
            }
            return;
        }
        // Only check URL parameters on the very first mount
        if (!isInitialMount.current) {
            // Select first default scenario when coming back to scenario selection (New Chat)
            if (selectedScenario === null && scenarios.length > 0) {
                // Clear URL parameters when returning to scenario selection (New Chat)
                const url = new URL(window.location.href);
                url.searchParams.delete("scenarioGroup");
                url.searchParams.delete("scenarioId");
                window.history.replaceState({}, "", url.toString());

                // Collapse all groups except "Bring Your Own Scenario"
                setExpandedGroups({ "Bring Your Own Scenario": true });

                const defaultScenarios = groupedScenarios["Default"] || [];
                if (defaultScenarios.length > 0) {
                    handleScenarioClick(defaultScenarios[0].scenarioId);
                }

                // Scroll to top of the page
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
            return;
        }

        // Check URL parameters first, even before scenarios load
        const urlParams = new URLSearchParams(window.location.search);
        const scenarioGroupParam = urlParams.get("scenarioGroup");
        const scenarioIdParam = urlParams.get("scenarioId");

        // If scenarioGroup is in URL, immediately notify parent with a placeholder to prevent default selection
        if (scenarioGroupParam && scenarios.length > 0) {
            // Find the group and select first scenario immediately
            const hasGroup = Object.keys(groupedScenarios).some((g) => g.toLowerCase() === scenarioGroupParam.toLowerCase());
            if (hasGroup) {
                const exactGroupName = Object.keys(groupedScenarios).find((g) => g.toLowerCase() === scenarioGroupParam.toLowerCase());
                if (exactGroupName) {
                    const groupScenarios = groupedScenarios[exactGroupName];
                    if (groupScenarios && groupScenarios.length > 0) {
                        // Immediately notify parent before any other logic
                        onScenarioSelect(groupScenarios[0].scenarioId);
                    }
                }
            }
        }

        // If scenarioId is in URL, immediately notify parent to prevent default selection
        if (scenarioIdParam) {
            const scenarioId = parseInt(scenarioIdParam, 10);

            // Immediately call onScenarioSelect to override any default selection in parent
            onScenarioSelect(scenarioId);

            // Wait for scenarios to load before setting prompts
            if (scenarios.length === 0) return;

            const scenario = scenarios.find(s => s.scenarioId === scenarioId);

            if (scenario) {
                const prompts = getSamplePrompts(scenarioId, scenarios);
                setCurrentPrompts(prompts);

                if (onSetScenarioInfo) {
                    onSetScenarioInfo(scenario.scenarioId, scenario.scenarioName, scenario.scenarioGrouping);
                }

                setShowSamplePrompts(true);
                setShowAllPrompts(false);
                setLocalInputValue("");
            }
            isInitialMount.current = false;
        }
        // If scenarioGroup is in URL, expand that accordion group and select first scenario
        else if (scenarioGroupParam) {
            // Wait for scenarios to load
            if (scenarios.length === 0) return;
            // Wait for groupedScenarios to be populated
            if (Object.keys(groupedScenarios).length === 0) return;

            const hasGroup = Object.keys(groupedScenarios).some((g) => g.toLowerCase() === scenarioGroupParam.toLowerCase());

            if (hasGroup) {
                // Find the exact group name (case-sensitive match)
                const exactGroupName = Object.keys(groupedScenarios).find((g) => g.toLowerCase() === scenarioGroupParam.toLowerCase());

                if (exactGroupName) {
                    // Select the first scenario in this group as default
                    const groupScenarios = groupedScenarios[exactGroupName];
                    if (groupScenarios && groupScenarios.length > 0) {
                        const firstScenarioId = groupScenarios[0].scenarioId;
                        // Immediately notify parent to set the scenario
                        onScenarioSelect(firstScenarioId);
                        setCurrentPrompts(getSamplePrompts(firstScenarioId, scenarios));
                    }

                    setExpandedGroups({ [exactGroupName]: true });

                    // Scroll to the group after a short delay to allow rendering
                    setTimeout(() => {
                        const groupElement = document.getElementById(`scenario-group-${exactGroupName}`);
                        if (groupElement) {
                            groupElement.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                    }, 300);
                }
            } else {
                // If group doesn't exist, expand "Bring Your Own Scenario" as fallback and select first scenario
                setExpandedGroups({ "Bring Your Own Scenario": true });
                const bringYourOwnScenarios = groupedScenarios["Bring Your Own Scenario"];
                if (bringYourOwnScenarios && bringYourOwnScenarios.length > 0) {
                    const firstScenarioId = bringYourOwnScenarios[0].scenarioId;
                    onScenarioSelect(firstScenarioId);
                    setCurrentPrompts(getSamplePrompts(firstScenarioId, scenarios));
                }
            }
            isInitialMount.current = false;
        }
        // Default: expand "Bring Your Own Scenario" group on mount and select first scenario
        else {
            setExpandedGroups({ "Bring Your Own Scenario": true });
            // Select first scenario from "Bring Your Own Scenario" on initial load
            const bringYourOwnScenarios = groupedScenarios["Bring Your Own Scenario"] || [];
            if (bringYourOwnScenarios && bringYourOwnScenarios.length > 0) {
                handleScenarioClick(bringYourOwnScenarios[0].scenarioId);
            }
            isInitialMount.current = false;
        }
    }, [scenarios, groupedScenarios, onScenarioSelect, onSetScenarioInfo, directChat, selectedScenario]);

    const handleScenarioClick = (scenarioId: number) => {
        onScenarioSelect(scenarioId);
        setCurrentPrompts(getSamplePrompts(scenarioId, scenarios));
    };

    const handleNext = () => {
        if (selectedScenario !== null) {
            const selectedScenarioData = scenarios.find(s => s.scenarioId === selectedScenario);

            // Track Start Chat button click with scenario ID
            logEvent("StartChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
                scenarioId: selectedScenario
            });

            if (selectedScenarioData && onSetScenarioInfo) {
                onSetScenarioInfo(selectedScenarioData.scenarioId, selectedScenarioData.scenarioName, selectedScenarioData.scenarioGrouping);
            }

            // Add scenarioId to URL
            const url = new URL(window.location.href);
            url.searchParams.set("scenarioId", selectedScenario.toString());
            window.history.replaceState({}, "", url.toString());

            // Ensure prompts are loaded for the selected scenario
            const prompts = getSamplePrompts(selectedScenario, scenarios);
            setCurrentPrompts(prompts);
            setShowSamplePrompts(true);
            setShowAllPrompts(false); // Reset to show only 3 prompts initially
            setLocalInputValue(""); // Clear input when first showing Ask Me Anything
            onNext(); // Call the parent's next handler
        }
    };

    const handleBackToScenarios = () => {
        // Clear scenarioId from URL when going back to scenario selection
        const url = new URL(window.location.href);
        const scenarioGroupParam = url.searchParams.get("scenarioGroup");
        url.searchParams.delete("scenarioId");
        window.history.replaceState({}, "", url.toString());

        setShowSamplePrompts(false);
        setShowAllPrompts(false);
        setLocalInputValue(""); // Clear the input when going back

        // Scroll to the expanded accordion group after a short delay
        if (scenarioGroupParam) {
            setTimeout(() => {
                const groupElement = document.getElementById(`scenario-group-${scenarioGroupParam}`);
                if (groupElement) {
                    groupElement.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }, 300);
        } else {
            // If no group in URL, scroll to top (Default scenarios)
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }, 100);
        }
    };

    if (scenariosLoading || (directChat && scenarios.length > 0 && selectedScenario === null)) {
        return (
            <div style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "250px",
                flexDirection: "column"
            }}>
                <div
                    style={{
                        border: "4px solid #f3f3f3",
                        borderTop: "4px solid #0078D7",
                        borderRadius: "50%",
                        width: "50px",
                        height: "50px",
                        animation: "spin 1s linear infinite",
                        margin: "0 auto 20px",
                    }}
                />
                <div style={{
                    fontSize: "16px",
                    color: "#616161",
                    fontWeight: 500,
                    textAlign: "center"
                }}>
                    Loading scenarios...
                </div>
                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
                </style>
            </div>
        );
    }

    if (directChat && scenarios.length === 0) {
        return <div role="alert">CVC Portfolio Intelligence is unavailable. Please reload or contact support.</div>;
    }

    if ((directChat || showSamplePrompts) && selectedScenario !== null) {
        const selectedScenarioData = scenarios.find(s => s.scenarioId === selectedScenario);

        return (
            <div
                ref={containerRef}
                style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "24px",
                    padding: "32px",
                    overflowY: "auto",
                    animation: "fadeInUp 0.5s ease-out",
                    margin: "0 auto",
                    boxSizing: "border-box",
                    background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)",
                    borderRadius: "20px",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)"
                }}>
                {/* Header with back button and view details */}
                {!directChat && <div style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px"
                }}>
                    <DefaultButton
                        iconProps={{ iconName: "ChevronLeft" }}
                        text="Back to Scenarios"
                        onClick={handleBackToScenarios}
                        styles={{
                            root: {
                                borderRadius: "12px",
                                padding: "12px 20px",
                                border: "2px solid #e1e4e8",
                                background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                fontWeight: 500,
                                ":hover": {
                                    transform: "translateX(-2px) scale(1.02)",
                                    borderColor: "#0078D7",
                                    background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%)",
                                    boxShadow: "0 4px 12px rgba(0, 120, 215, 0.2)"
                                }
                            }
                        }}
                    />
                    <DefaultButton
                        text="View Details"
                        iconProps={{ iconName: "Info" }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (selectedScenario !== null) {
                                const scenarioName = selectedScenarioData?.scenarioName || scenarios.find(s => s.scenarioId === selectedScenario)?.scenarioName || "Scenario";

                                // Log View Details button click
                                logEvent("ViewScenarioDetailsButton_Clicked", "AI Hub - Copilot Chat", "ScenarioSelection", {
                                    scenarioId: selectedScenario,
                                    scenarioName: scenarioName,
                                    calledFrom: "ScenarioSelection"
                                });

                                setSelectedScenarioDetails({
                                    id: selectedScenario,
                                    name: scenarioName
                                });
                                setIsPanelOpen(true);
                            }
                        }}
                        styles={{
                            root: {
                                borderRadius: "12px",
                                padding: "12px 20px",
                                border: "2px solid #e1e4e8",
                                background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                fontWeight: 500,
                                ":hover": {
                                    transform: "translateX(-2px) scale(1.02)",
                                    borderColor: "#0078D7",
                                    background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%)",
                                    boxShadow: "0 4px 12px rgba(0, 120, 215, 0.2)"
                                }
                            }
                        }}
                    />
                </div>}

                {/* Selected Scenario Badge with tooltip showing description */}
                {!directChat && <TooltipHost
                    content={(selectedScenarioData?.description || "").replace(/\\n/g, "\n")}
                    calloutProps={{
                        directionalHint: DirectionalHint.topCenter,
                        directionalHintFixed: true,
                        isBeakVisible: true,
                        beakWidth: 12,
                        gapSpace: 6,
                        styles: {
                            calloutMain: {
                                color: "white",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                fontSize: "13px",
                                maxWidth: ((selectedScenarioData?.description || "").replace(/\\n/g, " ").length > 300) ? "520px" : "380px",
                                whiteSpace: "pre-wrap",
                                wordWrap: "break-word",
                                lineHeight: "1.4",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                            }
                        }
                    }}
                    styles={{ root: { display: "inline-flex" } }}
                >
                    <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "12px 20px",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        borderRadius: "14px",
                        boxShadow: "0 6px 16px rgba(102, 126, 234, 0.35), 0 2px 4px rgba(102, 126, 234, 0.2)",
                        marginBottom: "10px"
                    }}>
                        <Icon iconName="Lightbulb" style={{ fontSize: "16px", color: "#fff" }} />
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{
                                fontSize: "10px",
                                color: "rgba(255, 255, 255, 0.8)",
                                fontWeight: 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                            }}>
                                {selectedScenarioData?.scenarioGrouping || scenarios.find(s => s.scenarioId === selectedScenario)?.scenarioGrouping || "Scenario"}
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{
                                    fontSize: "14px",
                                    color: "#fff",
                                    fontWeight: 600,
                                }}>
                                    {selectedScenarioName || selectedScenarioData?.scenarioName || scenarios.find(s => s.scenarioId === selectedScenario)?.scenarioName || "Default"}
                                </span>
                                {selectedScenarioData?.state && selectedScenarioData.state?.toUpperCase() === "PREVIEW" && (
                                    <span
                                        style={{
                                            fontSize: "9px",
                                            color: "rgba(255, 255, 255, 0.7)",
                                            fontWeight: 500,
                                            padding: "2px 6px",
                                            background: "rgba(255, 255, 255, 0.2)",
                                            borderRadius: "4px",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.3px",
                                        }}
                                    >
                                        {selectedScenarioData.state}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </TooltipHost>}

                {/* Ask Me Anything Title */}
                <h1 style={{
                    fontSize: "28px",
                    fontWeight: 600,
                    margin: "0 0 20px 0",
                    color: "#1a1a1a",
                    textAlign: "center"
                }}>
                    Ask Me Anything
                </h1>

                {/* Chat Input */}
                <div style={{ width: "100%", marginBottom: "30px" }}>
                    {ChatInput && (
                        <ChatInput
                            {...inputProps}
                            input={localInputValue}
                            onInputChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                const stringValue = String(e.target.value || "");
                                setLocalInputValue(stringValue);
                                if (inputProps?.onInputChange) {
                                    inputProps.onInputChange(e);
                                }
                            }}
                            scenarioId={selectedScenario}
                            scenarioName={selectedScenarioData?.scenarioName || scenarios.find(s => s.scenarioId === selectedScenario)?.scenarioName}
                        />
                    )}
                </div>

                {/* Sample Prompts */}
                <div style={{ width: "100%" }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "20px",
                        width: "100%",
                        maxWidth: "1000px",
                        margin: "0 auto 20px auto"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                        }}>
                            <Icon iconName="MessageFill" style={{ fontSize: "16px", color: "#0078D7" }} />
                            <h3 style={{
                                margin: 0,
                                fontSize: "18px",
                                fontWeight: 600,
                                color: "#242424"
                            }}>
                                {selectedScenarioData?.scenarioName
                                    ?.toLowerCase()
                                    ?.includes("purview migration")
                                    ? "Recommended prompt structures/topics to choose from"
                                    : "Sample Prompts"}
                            </h3>
                        </div>

                        {((selectedScenarioHasCategory && parsedScenarioPrompts.length > 3) || (!selectedScenarioHasCategory && currentPrompts.length > 3)) && (
                            <DefaultButton
                                text={showAllPrompts ? "Show Less" : "See More"}
                                iconProps={{ iconName: showAllPrompts ? "ChevronUp" : "ChevronDown" }}
                                onClick={() => setShowAllPrompts(!showAllPrompts)}
                                styles={{
                                    root: {
                                        borderRadius: "12px",
                                        padding: "8px 16px",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        border: "1px solid #e1e4e8",
                                        background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                                        color: "#0078D7",
                                        transition: "all 0.3s ease",
                                        ":hover": {
                                            borderColor: "#0078D7",
                                            background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%)",
                                            transform: "scale(1.02)"
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>

                    {/* Category Tabs - Show only if selected scenario has promptCategory */}
                    {selectedScenarioHasCategory && promptCategories.length > 0 && (
                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "12px",
                            marginBottom: "24px",
                            width: "100%",
                            maxWidth: "1000px",
                            margin: "0 auto 24px auto"
                        }}>
                            {promptCategories.map((category) => (
                                <div
                                    key={category}
                                    onClick={() => setSelectedPromptCategory(
                                        selectedPromptCategory === category ? null : category
                                    )}
                                    style={{
                                        backgroundColor: selectedPromptCategory === category ? "#005597" : "white",
                                        border: "2px solid #005597",
                                        borderRadius: "20px",
                                        color: selectedPromptCategory === category ? "white" : "#005597",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        padding: "8px 20px",
                                        transition: "all 0.2s ease",
                                        whiteSpace: "nowrap"
                                    }}
                                    onMouseEnter={(e) => {
                                        if (selectedPromptCategory !== category) {
                                            e.currentTarget.style.backgroundColor = "rgba(0, 85, 151, 0.08)";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (selectedPromptCategory !== category) {
                                            e.currentTarget.style.backgroundColor = "white";
                                        }
                                    }}
                                >
                                    {category}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Prompt Cards from Scenarios (with categories) - Only for scenarios with promptCategory */}
                    {selectedScenarioHasCategory && parsedScenarioPrompts.length > 0 ? (
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "20px",
                            width: "100%",
                            maxWidth: "1000px",
                            margin: "0 auto"
                        }}>
                            {(showAllPrompts ? filteredParsedPrompts : filteredParsedPrompts.slice(0, 3)).map((prompt) => (
                                <div
                                    key={prompt.id}
                                    style={{
                                        padding: "16px 20px",
                                        backgroundColor: "#f0f8ff",
                                        borderLeft: "4px solid #005597",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        display: "flex",
                                        flexDirection: "column"
                                    }}
                                    onClick={() => {
                                        logEvent("SamplePromptFromScenario_Selected", "AI Hub - Copilot Chat", "ScenarioSelection", {
                                            scenarioId: prompt.scenarioId,
                                            promptId: prompt.id,
                                            promptTitle: prompt.title,
                                            category: prompt.promptCategory
                                        });
                                        // Only send description (body after colon) to text area, not the title
                                        setLocalInputValue(prompt.description);
                                        if (onPromptSelect) {
                                            onPromptSelect(prompt.description);
                                        }
                                        containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-3px)";
                                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 120, 215, 0.2)";
                                        e.currentTarget.style.borderLeftColor = "#0078D7";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "none";
                                        e.currentTarget.style.borderLeftColor = "#005597";
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <h4 style={{
                                            margin: "0 0 12px 0",
                                            color: "#005597",
                                            fontSize: "15px",
                                            fontWeight: 600,
                                            lineHeight: 1.4
                                        }}>
                                            {prompt.title}
                                        </h4>
                                        <p style={{
                                            margin: 0,
                                            color: "#323130",
                                            fontSize: "14px",
                                            lineHeight: 1.5,
                                            overflow: "hidden",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical"
                                        }}>
                                            {prompt.description}
                                        </p>
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        marginTop: "12px"
                                    }}>
                                        <Link
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(prompt.promptText);
                                            }}
                                            styles={{
                                                root: {
                                                    color: "#005597",
                                                    fontSize: "13px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px"
                                                }
                                            }}
                                        >
                                            <span>↗</span> Copy prompt
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : currentPrompts.length > 0 ? (
                        /* Fallback to original sample prompts - same style as category-based */
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "20px",
                            width: "100%",
                            maxWidth: "1000px",
                            margin: "0 auto"
                        }}>
                            {(showAllPrompts ? currentPrompts : currentPrompts.slice(0, 3)).map((prompt, index) => {
                                // Parse title and description from prompt
                                const colonIndex = prompt.indexOf(':');
                                const hasTitle = colonIndex > 0 && colonIndex < 60;
                                const title = hasTitle ? prompt.substring(0, colonIndex).trim() : "";
                                const description = hasTitle ? prompt.substring(colonIndex + 1).trim() : prompt;

                                return (
                                    <div
                                        key={index}
                                        style={{
                                            padding: "16px 20px",
                                            backgroundColor: "#f0f8ff",
                                            borderLeft: "4px solid #005597",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            display: "flex",
                                            flexDirection: "column"
                                        }}
                                        onClick={() => {
                                            logEvent("SamplePromptFromScenario_Selected", "AI Hub - Copilot Chat", "ScenarioSelection", {
                                                scenarioId: selectedScenario,
                                                scenarioName: selectedScenarioData?.scenarioName,
                                                promptIndex: index,
                                                promptText: prompt.substring(0, 100),
                                                calledFrom: "ScenarioSelection"
                                            });
                                            // Only send description (body after colon) to text area, not the title
                                            setLocalInputValue(description);
                                            if (onPromptSelect) {
                                                onPromptSelect(description);
                                            }
                                            containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translateY(-3px)";
                                            e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 120, 215, 0.2)";
                                            e.currentTarget.style.borderLeftColor = "#0078D7";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "none";
                                            e.currentTarget.style.borderLeftColor = "#005597";
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            {hasTitle && (
                                                <h4 style={{
                                                    margin: "0 0 12px 0",
                                                    color: "#005597",
                                                    fontSize: "15px",
                                                    fontWeight: 600,
                                                    lineHeight: 1.4
                                                }}>
                                                    {title}
                                                </h4>
                                            )}
                                            <p style={{
                                                margin: 0,
                                                color: "#323130",
                                                fontSize: "14px",
                                                lineHeight: 1.5,
                                                overflow: "hidden",
                                                display: "-webkit-box",
                                                WebkitLineClamp: hasTitle ? 3 : 5,
                                                WebkitBoxOrient: "vertical"
                                            }}>
                                                {description}
                                            </p>
                                        </div>
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            marginTop: "12px"
                                        }}>
                                            <Link
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigator.clipboard.writeText(description);
                                                }}
                                                styles={{
                                                    root: {
                                                        color: "#005597",
                                                        fontSize: "13px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "4px"
                                                    }
                                                }}
                                            >
                                                <span>↗</span> Copy prompt
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{
                            textAlign: "center",
                            padding: "40px",
                            color: "#666",
                            fontSize: "16px"
                        }}>
                            <p>No sample prompts available for this scenario.</p>
                        </div>
                    )}
                </div>

                {/* Scenario Details Panel */}
                <ScenarioDetailsPanel
                    isOpen={isPanelOpen}
                    onDismiss={() => {
                        setIsPanelOpen(false);
                        setSelectedScenarioDetails(null);
                    }}
                    scenarioName={selectedScenarioDetails?.name || null}
                    scenarioId={selectedScenarioDetails?.id !== undefined ? selectedScenarioDetails.id : null}
                    scenarioDescription={
                        selectedScenarioDetails?.id !== undefined
                            ? scenarios.find(s => s.scenarioId === selectedScenarioDetails.id)?.description || null
                            : null
                    }
                    scenarioDetails={scenarioDetails}
                    onStartChat={selectedScenarioDetails?.id !== undefined ? handleNext : undefined}
                    isSelected={selectedScenario === selectedScenarioDetails?.id}
                />
            </div>
        );
    }

    const toggleGroup = (group: string) => {
        setExpandedGroups(prev => {
            // If clicking on already expanded group, close it
            // Otherwise, close all and open only the clicked one
            if (prev[group]) {
                // Closing the group - remove scenarioGroup from URL
                const url = new URL(window.location.href);
                url.searchParams.delete("scenarioGroup");
                window.history.replaceState({}, "", url.toString());
                return { [group]: false };
            }
            // Opening a group - add scenarioGroup to URL and select first scenario
            const url = new URL(window.location.href);
            url.searchParams.set("scenarioGroup", group);
            window.history.replaceState({}, "", url.toString());

            // Select the first scenario in this group
            const groupScenarios = groupedScenarios[group];
            if (groupScenarios && groupScenarios.length > 0) {
                handleScenarioClick(groupScenarios[0].scenarioId);
            }

            // Scroll to the group smoothly after a short delay to allow rendering
            setTimeout(() => {
                const groupElement = document.getElementById(`scenario-group-${group}`);
                if (groupElement) {
                    groupElement.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }, 100);

            return { [group]: true };
        });
    };

    // Get all groups and sort them ("Bring Your Own Scenario" first, then others alphabetically)
    const allGroups = Object.keys(groupedScenarios).sort((a, b) => {
        if (a === "Bring Your Own Scenario") return -1;
        if (b === "Bring Your Own Scenario") return 1;
        return a.localeCompare(b);
    });

    // Main scenario selection view
    return (
        <div style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "24px",
            padding: "20px 40px 100px 40px",
            animation: "fadeInUp 0.5s ease-out",
            margin: "0 auto",
            boxSizing: "border-box",
            position: "relative"
        }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "12px" }}>
                <h1 style={{
                    fontSize: size.mainHeaderSize,
                    fontWeight: 700,
                    margin: "0 0 12px 0",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text"
                }}>
                    Choose Your Scenario
                </h1>
                <p style={{
                    fontSize: size.mainSubtitleSize,
                    color: "#616161",
                    margin: 0,
                    lineHeight: 1.5,
                    fontWeight: 400
                }}>
                    Choose a scenario or select a previous conversation to continue
                </p>
            </div>

            {/* All Scenarios - Accordion Style (including Default) */}
            {allGroups.length > 0 && (
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
                    {allGroups.map((group, groupIndex) => {
                        const isExpanded = expandedGroups[group];
                        const groupScenarios = groupedScenarios[group];

                        return (
                            <div
                                key={group}
                                id={`scenario-group-${group}`}
                                style={{
                                    width: "100%",
                                    background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                                    border: "2px solid #e1e7f5",
                                    borderRadius: "20px",
                                    overflow: "hidden",
                                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                                    transition: "all 0.3s ease",
                                    animation: `fadeInUp 0.6s ease-out ${groupIndex * 0.1}s both`
                                }}
                            >
                                {/* Accordion Header */}
                                <div
                                    onClick={() => toggleGroup(group)}
                                    style={{
                                        padding: size.accordionPadding,
                                        cursor: "pointer",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        background: isExpanded
                                            ? "linear-gradient(135deg, #f0f7ff 0%, #e6f2ff 100%)"
                                            : "transparent",
                                        borderBottom: isExpanded ? "2px solid #e1e7f5" : "none",
                                        transition: "all 0.3s ease"
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isExpanded) {
                                            e.currentTarget.style.background = "linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isExpanded) {
                                            e.currentTarget.style.background = "transparent";
                                        }
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                        <div style={{
                                            width: size.groupIconSize,
                                            height: size.groupIconSize,
                                            borderRadius: "10px",
                                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)"
                                        }}>
                                            <Icon iconName="ViewList" style={{ fontSize: `${parseInt(size.groupIconSize) * 0.5}px`, color: "white" }} />
                                        </div>
                                        <div>
                                            <h3 style={{
                                                fontSize: size.groupHeaderSize,
                                                fontWeight: 600,
                                                color: "#242424",
                                                margin: 0
                                            }}>
                                                {group}
                                            </h3>
                                            <p style={{
                                                fontSize: size.groupSubtitleSize,
                                                color: "#616161",
                                                margin: "4px 0 0 0"
                                            }}>
                                                {groupScenarios.length} scenario{groupScenarios.length !== 1 ? "s" : ""}
                                            </p>
                                        </div>
                                    </div>
                                    <Icon
                                        iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                                        style={{
                                            fontSize: "20px",
                                            color: "#616161",
                                            transition: "transform 0.3s ease"
                                        }}
                                    />
                                </div>

                                {/* Accordion Content */}
                                {isExpanded && (
                                    <div style={{
                                        padding: "24px",
                                        display: "grid",
                                        gridTemplateColumns: groupScenarios.length === 1 ? "1fr" : "repeat(auto-fill, minmax(450px, 1fr))",
                                        maxWidth: "100%",
                                        gridAutoFlow: "row",
                                        gap: "20px",
                                        animation: "expandDown 0.3s ease-out"
                                    }} className="scenario-grid">
                                        {groupScenarios.map((scenario, index) => {
                                            const isSelected = selectedScenario === scenario.scenarioId;
                                            const displayDescription = scenario.description.replace(/\\n/g, " ");

                                            return (
                                                <div
                                                    key={scenario.scenarioId}
                                                    onClick={() => handleScenarioClick(scenario.scenarioId)}
                                                    style={{
                                                        padding: size.cardPadding,
                                                        background: isSelected
                                                            ? "linear-gradient(135deg, #e3f2fd 0%, #d1e7fd 100%)"
                                                            : "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                                                        border: isSelected
                                                            ? "3px solid #0078D7"
                                                            : "2px solid #e1e7f5",
                                                        borderRadius: size.borderRadius,
                                                        cursor: "pointer",
                                                        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                                        boxShadow: isSelected
                                                            ? "0 8px 25px rgba(0, 120, 215, 0.25)"
                                                            : "0 4px 15px rgba(0, 0, 0, 0.08)",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                                                        transform: isSelected ? "translateY(-8px) scale(1.03)" : "translateY(0) scale(1)",
                                                        position: "relative",
                                                        overflow: "hidden"
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) {
                                                            e.currentTarget.style.borderColor = "#0078D7";
                                                            e.currentTarget.style.boxShadow = "0 10px 25px rgba(0, 120, 215, 0.2)";
                                                            e.currentTarget.style.transform = "translateY(-6px) scale(1.02)";
                                                            e.currentTarget.style.background = "linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%)";
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSelected) {
                                                            e.currentTarget.style.borderColor = "#e1e7f5";
                                                            e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.08)";
                                                            e.currentTarget.style.transform = "translateY(0) scale(1)";
                                                            e.currentTarget.style.background = "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)";
                                                        }
                                                    }}
                                                >
                                                    {isSelected && (
                                                        <div style={{
                                                            position: "absolute",
                                                            top: "16px",
                                                            right: "16px",
                                                            width: size.checkmarkSize,
                                                            height: size.checkmarkSize,
                                                            borderRadius: "50%",
                                                            background: "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            animation: "scaleIn 0.3s ease-out"
                                                        }}>
                                                            <Icon iconName="CheckMark" style={{ fontSize: size.iconSize.check, color: "white" }} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: size.gap, marginBottom: size.marginBottom }}>
                                                            <div style={{
                                                                width: size.iconBoxSize,
                                                                height: size.iconBoxSize,
                                                                borderRadius: "8px",
                                                                background: isSelected
                                                                    ? "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)"
                                                                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                flexShrink: 0,
                                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)"
                                                            }}>
                                                                <Icon iconName="Lightbulb" style={{ fontSize: size.iconSize.lightbulb, color: "white" }} />
                                                            </div>
                                                            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}>
                                                                <h3 style={{
                                                                    margin: 0,
                                                                    fontSize: size.titleSize,
                                                                    fontWeight: 600,
                                                                    color: isSelected ? "#0078D7" : "#242424",
                                                                    lineHeight: 1.3,
                                                                }}>
                                                                    {scenario.scenarioName}
                                                                </h3>
                                                                {scenario.state && scenario.state.toUpperCase() === "PREVIEW" && (
                                                                    <span
                                                                        style={{
                                                                            fontSize: size.previewBadge,
                                                                            color: isSelected ? "#0078D7" : "#666",
                                                                            fontWeight: 600,
                                                                            padding: "3px 8px",
                                                                            background: isSelected
                                                                                ? "rgba(0, 120, 215, 0.1)"
                                                                                : "rgba(102, 126, 234, 0.1)",
                                                                            borderRadius: "4px",
                                                                            textTransform: "uppercase",
                                                                            letterSpacing: "0.5px",
                                                                            border: isSelected
                                                                                ? "1px solid rgba(0, 120, 215, 0.3)"
                                                                                : "1px solid rgba(102, 126, 234, 0.3)",
                                                                        }}
                                                                    >
                                                                        {scenario.state}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <TooltipHost
                                                            content={scenario.description.replace(/\\n/g, "\n")}
                                                            calloutProps={{
                                                                directionalHint: DirectionalHint.topCenter,
                                                                directionalHintFixed: true,
                                                                isBeakVisible: true,
                                                                beakWidth: 12,
                                                                gapSpace: 6,
                                                                styles: {
                                                                    calloutMain: {
                                                                        color: "white",
                                                                        borderRadius: "8px",
                                                                        padding: "12px 16px",
                                                                        fontSize: size.descriptionSize,
                                                                        maxWidth: (scenario.description.replace(/\\n/g, " ").length > 300) ? "520px" : "380px",
                                                                        whiteSpace: "pre-wrap",
                                                                        wordWrap: "break-word",
                                                                        lineHeight: "1.4",
                                                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            <p style={{
                                                                margin: 0,
                                                                fontSize: size.descriptionSize,
                                                                color: "#616161",
                                                                lineHeight: 1.4,
                                                                overflow: "hidden",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: groupScenarios.length >= 2 ? 3 : 2,
                                                                WebkitBoxOrient: "vertical",
                                                                textOverflow: "ellipsis",
                                                                cursor: "help"
                                                            }}>
                                                                {displayDescription}
                                                            </p>
                                                        </TooltipHost>
                                                    </div>
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        marginTop: "12px",
                                                        gap: "8px"
                                                    }}>
                                                        <div style={{
                                                            fontSize: size.samplePromptsSize,
                                                            color: isSelected ? "#0078D7" : "#999",
                                                            fontWeight: 500,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "6px"
                                                        }}>
                                                            <Icon iconName="MessageFill" style={{ fontSize: size.samplePromptsSize }} />
                                                            {getSamplePrompts(scenario.scenarioId, scenarios).length} sample prompts
                                                        </div>
                                                        {/* View Details Button */}
                                                        <DefaultButton
                                                            text="View Details"
                                                            iconProps={{ iconName: "Info" }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedScenarioDetails({
                                                                    id: scenario.scenarioId,
                                                                    name: scenario.scenarioName
                                                                });
                                                                setIsPanelOpen(true);
                                                            }}
                                                            styles={{
                                                                root: {
                                                                    minWidth: "auto",
                                                                    height: size.buttonHeight,
                                                                    padding: "0 10px",
                                                                    borderRadius: "6px",
                                                                    border: isSelected ? "1px solid #0078D7" : "1px solid #e3f2fd",
                                                                    background: isSelected
                                                                        ? "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)"
                                                                        : "linear-gradient(135deg, #e3f2fd 0%, #d1e7fd 100%)",
                                                                    transition: "all 0.3s ease",
                                                                    boxShadow: isSelected ? "0 2px 8px rgba(0, 120, 215, 0.3)" : "none"
                                                                },
                                                                rootHovered: {
                                                                    background: "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)",
                                                                    border: "1px solid #0078D7",
                                                                    transform: "scale(1.05)",
                                                                    boxShadow: "0 2px 8px rgba(0, 120, 215, 0.3)"
                                                                },
                                                                label: {
                                                                    fontSize: size.buttonFontSize,
                                                                    fontWeight: 600,
                                                                    color: isSelected ? "white" : "#0078D7",
                                                                    margin: "0 4px 0 0"
                                                                },
                                                                labelHovered: {
                                                                    color: "white"
                                                                },
                                                                icon: {
                                                                    fontSize: size.buttonFontSize,
                                                                    color: isSelected ? "white" : "#0078D7"
                                                                },
                                                                iconHovered: {
                                                                    color: "white"
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Action Buttons - Centered at Bottom */}
            {selectedScenario !== null && (
                <div style={{
                    position: "fixed",
                    bottom: "32px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 1000,
                    animation: "slideUp 0.4s ease-out"
                }}>
                    <PrimaryButton
                        text="Start Chat"
                        onClick={handleNext}
                        iconProps={{ iconName: "ChevronRight" }}
                        styles={{
                            root: {
                                borderRadius: size.borderRadius,
                                padding: size.startButtonPadding,
                                fontSize: size.startButtonSize,
                                fontWeight: 600,
                                background: "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)",
                                border: "none",
                                boxShadow: "0 8px 24px rgba(0, 120, 215, 0.4), 0 4px 12px rgba(0, 120, 215, 0.2)",
                                transition: "all 0.3s ease",
                                minWidth: screenSize === "mobile" ? "180px" : "220px",
                                height: size.startButtonHeight,
                                ":hover": {
                                    transform: "translateY(-2px) scale(1.02)",
                                    boxShadow: "0 12px 32px rgba(0, 120, 215, 0.5), 0 6px 16px rgba(0, 120, 215, 0.3)",
                                    background: "linear-gradient(135deg, #106ebe 0%, #0078D7 100%)"
                                },
                                ":active": {
                                    transform: "translateY(0px) scale(0.98)"
                                }
                            }
                        }}
                    />
                </div>
            )}

            <style>
                {`
                    @keyframes fadeInUp {
                        0% { opacity: 0; transform: translateY(20px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    
                    @keyframes scaleIn {
                        0% { transform: scale(0); }
                        100% { transform: scale(1); }
                    }
                    
                    @keyframes expandDown {
                        0% { 
                            opacity: 0;
                            max-height: 0;
                            transform: scaleY(0.95);
                        }
                        100% { 
                            opacity: 1;
                            max-height: 2000px;
                            transform: scaleY(1);
                        }
                    }
                    
                    @keyframes slideUp {
                        0% { 
                            opacity: 0;
                            transform: translateX(-50%) translateY(20px);
                        }
                        100% { 
                            opacity: 1;
                            transform: translateX(-50%) translateY(0);
                        }
                    }
                    
                    /* Custom scrollbar for accordion content */
                    div::-webkit-scrollbar {
                        width: 8px;
                    }
                    
                    div::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 10px;
                    }
                    
                    div::-webkit-scrollbar-thumb {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        border-radius: 10px;
                    }
                    
                    div::-webkit-scrollbar-thumb:hover {
                        background: linear-gradient(135deg, #0078D7 0%, #106ebe 100%);
                    }
                    
                    /* Limit scenario grid to maximum 2 columns */
                    .scenario-grid {
                        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    }
                    
                    @media (min-width: 900px) {
                        .scenario-grid {
                            grid-template-columns: repeat(2, 1fr);
                        }
                    }
                `}
            </style>

            {/* Scenario Details Panel */}
            <ScenarioDetailsPanel
                isOpen={isPanelOpen}
                onDismiss={() => {
                    setIsPanelOpen(false);
                    setSelectedScenarioDetails(null);
                }}
                scenarioName={selectedScenarioDetails?.name || null}
                scenarioId={selectedScenarioDetails?.id !== undefined ? selectedScenarioDetails.id : null}
                scenarioDescription={
                    selectedScenarioDetails?.id !== undefined
                        ? scenarios.find(s => s.scenarioId === selectedScenarioDetails.id)?.description || null
                        : null
                }
                scenarioDetails={scenarioDetails}
                onStartChat={selectedScenarioDetails?.id !== undefined ? handleNext : undefined}
                isSelected={selectedScenario === selectedScenarioDetails?.id}
            />
        </div>
    );
};

export default ScenarioSelection;